import PersonalOrderTracker from './PersonalOrdersTracker';
import { CreditCard } from '../../lib/CreditCard';

$(() => {
  class BaseForm {
    el: JQuery;
    excludeFieldNames: string[];

    constructor(el: JQuery, excludeFieldNames: string[]) {
      this.el = el;
      this.excludeFieldNames = excludeFieldNames;
    }

    validate() {
      return this._validateWithServer();
    }

    submit() {
      return $.post(this.el.attr('action') || '', this.serialize());
    }

    // NOTE: We never send a credit card information in all kinds of forms
    serialize(): string {
      const filtered = this.el.serializeArray().filter(
        ({ name, value }) =>
          !this.excludeFieldNames.includes(name) &&
          (name !== 'order[coupon_code]' || value !== '') && // NOTE: Ad-hoc fix to remove duplicated key-value pair from FormData.
          (name !== 'order[tracking_code]' || value !== ''), // Don't send tracking_code if it is empty, not to send both coupon code and tracking code for basic plan
      );
      const appended = filtered.concat(this._additionalFormData());
      return $.param(appended);
    }

    _validateWithServer(): any {
      return $.post(this.el.data('validation-path'), this.serialize());
    }

    _additionalFormData(): any {
      return [];
    }
  }

  class CreditCardTokenForm extends BaseForm {
    private static readonly FIELD_NAME_TOKEN = 'order[sbps_credit_card_token]';
    private static readonly FIELD_NAME_TOKEN_KEY =
      'order[sbps_credit_card_token_key]';

    private creditCard: CreditCard;
    private token: string | null;
    private tokenKey: string | null;

    constructor(
      el: JQuery,
      excludeFieldNames: string[],
      creditCard: CreditCard,
      token: string | null = null,
      tokenKey: string | null = null,
    ) {
      super(el, excludeFieldNames);
      this.creditCard = creditCard;
      this.token = token;
      this.tokenKey = tokenKey;
    }

    validate() {
      const deferred = $.Deferred();

      if (!this.creditCard.isValid()) {
        return deferred.reject(this.creditCard.errors.fullMessages());
      }

      this.creditCard
        .generateToken()
        .then(this.setToken.bind(this))
        .then(this._validateWithServer.bind(this))
        .done(deferred.resolve)
        .fail(deferred.reject);

      return deferred.promise();
    }

    setToken(token: string, tokenKey: string): void {
      this.token = token;
      this.tokenKey = tokenKey;
    }

    _additionalFormData() {
      return [
        { name: CreditCardTokenForm.FIELD_NAME_TOKEN, value: this.token },
        {
          name: CreditCardTokenForm.FIELD_NAME_TOKEN_KEY,
          value: this.tokenKey,
        },
      ];
    }
  }

  class RegistrationPersonalOrderView {
    private ui: any;
    private remarkableLabels: any;
    private valid: boolean;
    private pageNames: string[];
    private plan: string | undefined;
    private tracker: PersonalOrderTracker | undefined;
    private form: CreditCardTokenForm | BaseForm | undefined;

    constructor() {
      this.ui = {
        rawForm: $($.rails.formSubmitSelector),
        spotOuter: $('.spot-outer'),
        wizard: $('.wizard'),
        paymentMethodRadio: $('input[name="order[payment_method]"]'),
        paymentTypeRadio: $('input[name="order[payment_type_code]"]'),
        couponCodeCredit: $(
          'input.payment-detail-credit-coupon-input[name="order[coupon_code]"]',
        ),
        couponCodeCarrier: $(
          'input.payment-detail-carrier-coupon-input[name="order[coupon_code]"]',
        ),
        trackingCode: $('input[name="order[tracking_code]"]'),
        alertRow: $('.alert-row'),
        backButton: $('.btn[data-move=back]'),
        freeTermAnnotation: $('.free-term-annotation'),
        campaignCarrierAnnotation: $('#campaign-carrier-annotation'),
        campaignWebcvsAnnotation: $('#campaign-webcvs-annotation'),
        // For confirmation page
        orderDetail: $('#order-detail'),
        cardNumber: $('#order_card_number'),
        cardYear: $('#order_card_year'),
        cardMonth: $('#order_card_month'),
        securityCode: $('#order_security_code'),
        // select
        selectContainer: $('.select2-container'),
        select: $('select'),
        // only when using template is `basic_only`
        basicOnlyApply: $('#basic-only-apply'),
        // only when using template is `basic_or_coaching`
        basicOrCoachingApply: $('.basic-or-coaching-apply'),
        // hidden field
        withDiscountPriceCampaign: $('#with_discount_price_campaign'),
        // guide to discount price campaign
        discountPriceCampaignGuide: $('#discount-price-campaign-guide'),
        // link to discount price campaign landing page url
        discountPriceCampaignLandingPageUrl: $(
          '#discount-price-campaign-landing-page-url',
        ),
        // disclaim on webcvs original price
        webcvsOriginalPriceDisclaim: $('#webcvs-original-price-disclaim'),
      };

      this.remarkableLabels = this.ui.orderDetail.data('remarkable-labels');
      this.valid = false;
      this.pageNames = ['choose_plan', 'payment_detail', 'confirm_order'];
      this.plan = undefined;
    }

    begin() {
      this.initializeHistoryHandlers();
      this.hideAlert();
      this.ui.freeTermAnnotation.hide();
      this.ui.campaignCarrierAnnotation.hide();
      this.ui.campaignWebcvsAnnotation.hide();

      this.tracker = new PersonalOrderTracker().begin();

      $('#payment-detail').on('qs.wizard.moving', this.validate.bind(this));
      this.ui.wizard.on('qs.wizard.shown', this.toggleSpotByStep.bind(this));
      this.ui.wizard.on('qs.wizard.shown', this.scrollToTop.bind(this));

      this.ui.rawForm.on('change', () => {
        this.valid = false;
        this.ui.orderDetail.empty();
      });
      this.ui.rawForm.on('submit', this.submit.bind(this));
      this.ui.rawForm.on('keypress', (e: JQuery.Event) => {
        // Prevent users from submitting a form by hitting Enter
        if (e.keyCode === 13) e.preventDefault();
      });

      this.ui.paymentMethodRadio.on(
        'change',
        this.toggleFreeTermAnnotation.bind(this),
      );
      this.ui.paymentMethodRadio.on(
        'change',
        this.toggleCampaignCarrierAnnotation.bind(this),
      );
      this.ui.paymentMethodRadio.on(
        'change',
        this.toggleCampaignWebcvsAnnotation.bind(this),
      );
      this.ui.paymentMethodRadio.on('change', (e: Event) =>
        this.colorSelectedCardInSamePage(e, '#choose-plan'),
      );
      this.ui.paymentTypeRadio.on('change', (e: Event) =>
        this.colorSelectedCardInSamePage(e, '#payment-detail'),
      );
      this.ui.paymentTypeRadio.on(
        'change',
        this.toggleAmountIfBasicCredit.bind(this),
      );

      // select2 does not allow us to use custom keypress event so we use keyup event
      // ref. https://github.com/select2/select2/blob/ebf10c93db7d6d7a0d1330119d4c6f32cbd231d7/src/js/select2/core.js#L350
      this.ui.selectContainer.on(
        'click keyup',
        this.toggleRequiredClass.bind(this),
      );
      this.ui.select.on('change', this.toggleHasValue.bind(this));

      this.ui.basicOnlyApply.on('click', this.onPlanDecided.bind(this));
      this.ui.basicOrCoachingApply.on('click', this.onPlanDecided.bind(this));
    }

    private initializeHistoryHandlers() {
      if (!window.history?.pushState) return;

      history.replaceState(null, '', `new#${this.pageNames[0]}`);

      this.ui.wizard.on('qs.wizard.shown', (_: JQuery.Event, data: any) => {
        if (data.currentIndex > data.previousIndex) {
          history.pushState(
            null,
            '',
            `new#${this.pageNames[data.currentIndex]}`,
          );
          window.krt?.('send', 'view');
        }
      });

      $(window).on('popstate', this.popStateHandler.bind(this));
      this.ui.backButton.on('click', this.callHistoryBack.bind(this));
    }

    private popStateHandler() {
      ($('.wizard') as any).wizard('back');
      window.krt?.('send', 'view');
    }

    private callHistoryBack(e: JQuery.Event) {
      // Go back via the popStateHandler instead of the wizard event
      // to ensure the same behavior for the following two cases:
      //   1. the user clicks their browser's back button
      //   2. the user clicks @ui.backButton which we provide on the page
      e.preventDefault();
      history.back(); // It calls popStateHandler
      window.krt?.('send', 'view');
    }

    private toggleSpotByStep(_: JQuery.Event, data: any) {
      let currentStep;
      switch (data.currentIndex) {
        case 0:
          currentStep = '.choose-plan-step';
          break;
        case 1:
        case 2:
          currentStep = '.payment-detail-step';
          break;
      }
      this.ui.spotOuter.removeClass('active');
      this.ui.spotOuter.filter(currentStep).addClass('active');
    }

    private scrollToTop(_: JQuery.Event, data: any) {
      if (data.previousIndex < data.currentIndex) this.scrollTo(0); // Scroll to form top only when step goes on
    }

    private scrollTo(top: number) {
      $('html,body').stop().animate({ scrollTop: top });
    }

    private getPaymentMethod() {
      return this.ui.paymentMethodRadio.filter(':checked').val();
    }

    private getPaymentType() {
      return this.ui.paymentTypeRadio.filter(':checked').val();
    }

    private onPlanDecided(e: Event) {
      if (!(e.currentTarget instanceof HTMLElement)) {
        return;
      }

      this.plan = $(e.currentTarget).data('plan');

      if (this.plan === 'basic') {
        this.togglePaymentDetailByPaymentMethod(
          this.plan,
          this.getPaymentMethod(),
        );
      } else if (this.plan === 'tokkun') {
        this.ui.paymentMethodRadio
          .filter('[value="credit"]')
          .prop('checked', true)
          .trigger('change');
        this.togglePaymentDetailByPaymentMethod(this.plan, 'credit');
      } else {
        throw new TypeError(`Unexpected plan (${this.plan}) is given.`);
      }
    }

    private toggleFreeTermAnnotation() {
      if (this.getPaymentMethod() === 'credit') {
        this.ui.freeTermAnnotation.hide();
      } else {
        this.ui.freeTermAnnotation.show();
      }
    }

    private toggleCampaignCarrierAnnotation() {
      if (this.getPaymentMethod() === 'carrier') {
        this.ui.campaignCarrierAnnotation.show();
      } else {
        this.ui.campaignCarrierAnnotation.hide();
      }
    }

    private toggleCampaignWebcvsAnnotation() {
      if (this.getPaymentMethod() === 'webcvs') {
        this.ui.campaignWebcvsAnnotation.show();
      } else {
        this.ui.campaignWebcvsAnnotation.hide();
      }
    }

    private togglePaymentDetailByPaymentMethod(
      // Reset parental_consent all of payment type
      plan: string,
      paymentMethod: string,
    ) {
      $('input[name="order[parental_consent]"]').each((_, el) => {
        $(el).prop('checked', false);
      });

      const $paymentDetail = $(
        `.payment-detail.payment-detail-${paymentMethod}`,
      );
      $paymentDetail.show();
      $(`.payment-detail:not(.payment-detail-${paymentMethod})`).hide();

      let dom;
      if (paymentMethod === 'credit') {
        $paymentDetail.find(`.plan-based.payment-detail-${plan}`).show();
        $paymentDetail.find(`.plan-based:not(.payment-detail-${plan})`).hide();
        dom = $(`.payment-detail-${plan}`).find(this.ui.paymentTypeRadio);
      } else {
        dom = this.ui.paymentTypeRadio;
      }

      $paymentDetail.find(dom).first().prop('checked', true).trigger('change');

      // Switch coupon code field
      this.ui.couponCodeCredit.val('');
      this.ui.couponCodeCarrier.val('');
      this.getCouponCode().prop('disabled', true);
      $paymentDetail.find(this.getCouponCode()).prop('disabled', false);

      this.ui.trackingCode.val('');
      this.ui.trackingCode.prop('disabled', true);
      $paymentDetail.find(this.ui.trackingCode).prop('disabled', false);

      this.tracker?.replaceYtmSelector(plan, this.getPaymentMethod());
    }

    private colorSelectedCardInSamePage(e: Event, pageId: string) {
      const $currentSelectedCard = $(pageId).find('.card.active:has(> label)');
      $currentSelectedCard.removeClass('active');

      if (!(e.currentTarget instanceof HTMLInputElement)) {
        return;
      }

      const $newlySelectedCard = $(e.currentTarget).closest('.card');
      $newlySelectedCard.addClass('active');
    }

    private toggleAmountIfBasicCredit(e: Event) {
      if (!(e.currentTarget instanceof HTMLInputElement)) {
        return;
      }
      const recurringTerm = $(e.currentTarget).data('recurring-term');
      if (!recurringTerm) return;

      $(`.credit-amount:not('.${recurringTerm}')`).hide();
      $(`.credit-amount.${recurringTerm}`).show();
    }

    private onCreateSuccess(data: any) {
      if (data.is_sbps_link_type) {
        const form_html = $(data.form_html);
        form_html.appendTo('body');
        form_html.submit().remove();
      } else {
        window.location.href = data.redirect_url;
      }
    }

    private onCreateFailure(xhr: any) {
      if (xhr.responseJSON?.redirect) {
        window.location.href = xhr.responseJSON?.redirect_url;
      } else {
        this.showAlert(xhr.responseJSON?.errors);
        $.rails.enableFormElements(this.ui.rawForm);
      }
    }

    private hideAlert() {
      this.ui.alertRow.slideUp().find('ul').empty();
    }

    private showAlert(errors: string[]) {
      const $ul = $('.wizard-step.active .alert-row ul');

      // When an unexpected error occurred, the server may not return proper message.
      // So we have to show a default message for that case.
      if (!errors) {
        errors = [$ul.data('default-error-message')];
      }

      for (const error of errors) {
        $ul.append(`<li>${error}</li>`);
      }
      this.ui.alertRow.slideDown();
    }

    private submit(e: JQuery.Event) {
      e.preventDefault();

      this.hideAlert();

      // @form was defined in @reNewForm
      this.form
        ?.submit()
        .done(this.onCreateSuccess.bind(this))
        .fail(this.onCreateFailure.bind(this));
    }

    private onValidateSuccess() {
      this.valid = true;
    }

    private getCouponCode() {
      return this.getPaymentMethod() === 'credit'
        ? this.ui.couponCodeCredit
        : this.ui.couponCodeCarrier;
    }

    private getOrderDetail() {
      const params = {
        'order[payment_type_code]': this.getPaymentType(),
        'order[coupon_code]': this.getCouponCode().val(),
        'order[tracking_code]': this.ui.trackingCode.val(),
        ef_entered_time: this.ui.rawForm.data('ef-entered-time'),
      };
      return $.get(this.ui.rawForm.data('order-detail-path'), params);
    }

    // @param {xhr|string[]} error
    private onFailure(error: any) {
      this.valid = false;
      const errorMessages = Array.isArray(error)
        ? error
        : error.responseJSON?.errors;
      this.showAlert(errorMessages);
    }

    private renderConfirmOrder(data: any) {
      $.each(data.basic_info, this.appendOrderDetailRow.bind(this));
      $.each(data.orders, (_: number, order: any) => {
        this.ui.orderDetail.append($('<hr>', { class: 'divider m-y-md' }));
        $.each(order, this.appendOrderDetailRow.bind(this));
      });
      this.ui.orderDetail.append($('<hr>', { class: 'divider m-y-md' }));
      if (data.payment_start)
        $.each(data.payment_start, this.appendOrderDetailRow.bind(this));
      if (data.webcvs_due)
        $.each(data.webcvs_due, this.appendOrderDetailRow.bind(this));
      this.ui.orderDetail.append($('<hr>', { class: 'divider m-y-md' }));
      $.each(data.amount_info, this.appendOrderDetailRow.bind(this));
      this.ui.withDiscountPriceCampaign.val(data.with_discount_price_campaign);
      if (data.discount_price_campaign_landing_page_url !== '') {
        this.ui.discountPriceCampaignLandingPageUrl.attr(
          'href',
          data.discount_price_campaign_landing_page_url,
        );
      }
      if (data.with_discount_price_campaign) {
        this.ui.discountPriceCampaignGuide.show();
      } else {
        this.ui.discountPriceCampaignGuide.hide();
      }
      if (this.getPaymentMethod() === 'webcvs' && data.original_basic_price) {
        this.ui.webcvsOriginalPriceDisclaim.show();
      } else {
        this.ui.webcvsOriginalPriceDisclaim.hide();
      }
    }

    private appendOrderDetailRow(label: string, value: any) {
      let rowClassName = 'row';
      if (this.remarkableLabels.includes(label)) {
        rowClassName += ' sapuri-blue bold';
      }
      const $row = $('<div>', { class: rowClassName });
      $row.append($('<div>', { class: 'col-xs-6', text: label }));
      const content = Array.isArray(value)
        ? value.map((v: string) => `<span>${v}</span>`).join('<br />')
        : value;
      $row.append(`<div class="col-xs-6">${content}</div>`);
      this.ui.orderDetail.append($row);
    }

    // Q. What is the `data` argument?
    // A. It's `qa.wizard.moving` event data defined in QStrap.
    //    Detail: https://github.com/quipper/qstrap/blob/1255cc1f02b6a56757818095cccbfbc674c700bb/src/js/qstrap/wizard.js.coffee
    private validate(_: JQuery.Event, data: any) {
      this.hideAlert();

      if (data.nextIndex < data.currentIndex || this.valid) return; // Do not validate when going back

      data.cancelling = true; // stop moving

      this.reNewForm();
      this.form
        ?.validate()
        .then(this.onValidateSuccess.bind(this))
        .then(this.getOrderDetail.bind(this))
        .then(this.renderConfirmOrder.bind(this))
        .done(() => {
          this.ui.wizard.wizard('next');
          this.tracker?.trackStep7(this.plan ?? '', this.getPaymentMethod());
        })
        .fail(this.onFailure.bind(this));
    }

    private reNewForm() {
      // NOTE: We never send a credit card information in all kinds of forms
      const excludeFieldNames = [
        this.ui.cardNumber,
        this.ui.cardYear,
        this.ui.cardMonth,
        this.ui.securityCode,
      ].map((e: JQuery<HTMLElement>) => e.attr('name') || '');

      // persist form for next wizard
      this.form =
        this.getPaymentMethod() === 'credit'
          ? new CreditCardTokenForm(
              this.ui.rawForm,
              excludeFieldNames,
              new CreditCard({
                number: this.ui.cardNumber.val(),
                expirationYear: this.ui.cardYear.val(),
                expirationMonth: this.ui.cardMonth.val(),
                securityCode: this.ui.securityCode.val(),
                errorMessageMap: $('#credit_card_error_messages_holder').data(
                  'credit-card-error-messages-definition',
                ),
              }),
            )
          : new BaseForm(this.ui.rawForm, excludeFieldNames);
    }

    private toggleRequiredClass(e: Event) {
      if (!(e.currentTarget instanceof HTMLElement)) {
        return;
      }

      const $select = $(e.currentTarget).prev();
      const hasClass =
        !$select.hasClass('has-value') && $select.prop('required');
      $('.select2-dropdown').toggleClass('required', hasClass);
    }

    private toggleHasValue(e: Event) {
      if (!(e.currentTarget instanceof HTMLElement)) {
        return;
      }
      const value = (e.target as HTMLInputElement).value;

      $(e.currentTarget).toggleClass('has-value', !!value);
    }
  }

  // activate
  if ($('#page_registration-personal_orders_new').is(':visible')) {
    const view = new RegistrationPersonalOrderView();
    view.begin();
  }
});
